import { useEffect, useReducer } from "react";
import { slides } from "./registry";

export interface DeckState {
  index: number;
  direction: 1 | -1;
  overview: boolean;
}

export type DeckAction =
  | { type: "NEXT" }
  | { type: "PREV" }
  | { type: "GOTO"; index: number }
  | { type: "TOGGLE_OVERVIEW" }
  | { type: "CLOSE_OVERVIEW" };

const clamp = (n: number) => Math.min(Math.max(n, 0), slides.length - 1);

function reducer(s: DeckState, a: DeckAction): DeckState {
  switch (a.type) {
    case "NEXT":
      return { ...s, index: clamp(s.index + 1), direction: 1, overview: false };
    case "PREV":
      return { ...s, index: clamp(s.index - 1), direction: -1, overview: false };
    case "GOTO":
      return {
        ...s,
        index: clamp(a.index),
        direction: a.index >= s.index ? 1 : -1,
        overview: false,
      };
    case "TOGGLE_OVERVIEW":
      return { ...s, overview: !s.overview };
    case "CLOSE_OVERVIEW":
      return { ...s, overview: false };
  }
}

const readHash = () => {
  const n = parseInt(window.location.hash.slice(1), 10);
  return Number.isFinite(n) ? clamp(n - 1) : 0;
};

export function useDeck(toggleFullscreen: () => void) {
  const [state, dispatch] = useReducer(reducer, undefined, () => ({
    index: readHash(),
    direction: 1 as const,
    overview: false,
  }));

  // index -> hash, so a refresh keeps the position (replaceState: no history spam)
  useEffect(() => {
    history.replaceState(null, "", `#${state.index + 1}`);
  }, [state.index]);

  // hash -> index (manual edits / deep links)
  useEffect(() => {
    const onHash = () => {
      const index = readHash();
      // Normalize garbage/out-of-range hashes even when the index doesn't
      // change (the index->hash effect only fires on actual changes).
      if (window.location.hash !== `#${index + 1}`) {
        history.replaceState(null, "", `#${index + 1}`);
      }
      dispatch({ type: "GOTO", index });
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Interactive demos opt out of deck navigation by calling e.preventDefault()
      if (e.defaultPrevented) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable]")) return;

      switch (e.key) {
        case "ArrowRight":
        case " ":
        case "PageDown":
          e.preventDefault();
          dispatch({ type: "NEXT" });
          break;
        case "ArrowLeft":
        case "PageUp":
          e.preventDefault();
          dispatch({ type: "PREV" });
          break;
        case "Home":
          e.preventDefault();
          dispatch({ type: "GOTO", index: 0 });
          break;
        case "End":
          e.preventDefault();
          dispatch({ type: "GOTO", index: slides.length - 1 });
          break;
        case "Escape":
          dispatch({ type: "TOGGLE_OVERVIEW" });
          break;
        case "f":
        case "F":
          toggleFullscreen();
          break;
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleFullscreen]);

  return { ...state, dispatch };
}
